import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Lock, CheckCircle2, List, ChevronDown, Trophy, BookOpen } from 'lucide-react';
import { positionLabel, TOPIC_TERM } from './eliteTopicSequence';

/**
 * EliteTopicNavigation — learner-facing topic sequencing UI (27-Aug-2026).
 *
 * All three pieces read the SAME `nav` object from resolveNavigation(), so
 * the header label, the bottom cards and the end-of-technology state can
 * never disagree about position, total, previous or next.
 *
 *   <TopicPosition nav />              "Topic 7 of 31" (secondary text)
 *   <TopicNavigationBar nav … />       bottom Previous / Next cards + centre label,
 *                                      section-crossing hints, lock badges, end state
 *   <OnThisPageDropdown headings … />  the right-hand outline as a collapsible control (< xl)
 *
 * This is TOPIC navigation (between documents). "On this page" navigates the
 * "## N. Title" headings INSIDE a document — a different level; both stay.
 */

const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED] focus-visible:ring-offset-2';

export const TopicPosition = ({ nav, className = '', isDark = false, term = TOPIC_TERM }) => {
  const label = positionLabel(nav, term);
  if (!label) return null;
  return (
    <span data-testid="topic-position" className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'} ${className}`} aria-label={`${term} ${nav.position} of ${nav.total}`}>
      {label}
    </span>
  );
};

const NavCard = ({ direction, entry, crossesSection, access, isDark, onGo, term }) => {
  const isNext = direction === 'next';
  const locked = !!(access && access.locked);
  const usesPaidUnit = !!(access && access.state === 'AVAILABLE');
  const kicker = crossesSection ? (isNext ? 'Next section' : 'Previous section') : (isNext ? `Next ${term}` : `Previous ${term}`);
  const aria = `Go to ${isNext ? 'next' : 'previous'} ${term.toLowerCase()}: ${entry.title}${crossesSection ? ` (${entry.sectionTitle})` : ''}${locked ? ' — locked' : ''}`;
  return (
    <button
      type="button"
      onClick={() => onGo(entry, { locked, access })}
      aria-label={aria}
      title={aria}
      data-testid={`topic-nav-${direction}`}
      className={`group flex-1 min-w-0 sm:max-w-[300px] flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl border transition-all ${focusRing} ${isNext ? 'justify-between text-right' : 'text-left'} ${
        isDark ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white border-gray-100 hover:border-purple-200 hover:shadow-lg'
      }`}
    >
      {!isNext && (
        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
          <ArrowLeft size={20} className="text-gray-400 group-hover:text-[#5B21B6] transition-colors" aria-hidden="true" />
        </div>
      )}
      <div className="min-w-0 overflow-hidden">
        <p className={`text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 ${crossesSection ? 'text-[#5B21B6]' : 'text-gray-400'}`}>
          {kicker}
          {locked && <Lock size={10} className="text-amber-500" aria-hidden="true" />}
        </p>
        <p className={`text-sm font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{entry.title}</p>
        <p className={`hidden sm:block text-[11px] font-semibold truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {crossesSection ? `${entry.sectionTitle} · ` : ''}{term} {entry.position}
          {locked ? ' · Locked' : usesPaidUnit ? ' · uses 1 paid page' : ''}
        </p>
      </div>
      {isNext && (
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${locked ? 'bg-amber-500/10' : 'bg-[#5B21B6]/10'}`}>
          {locked ? <Lock size={18} className="text-amber-500" aria-hidden="true" /> : <ArrowRight size={20} className="text-[#5B21B6]" aria-hidden="true" />}
        </div>
      )}
    </button>
  );
};

/**
 * Bottom navigation. `onGo(entry, { locked, access })` is the page's single
 * open-topic handler (it applies the existing lock/upgrade behaviour).
 * `onBackToTechnology` renders the end-of-technology action.
 */
export const TopicNavigationBar = ({ nav, isDark = false, onGo, onBackToTechnology, completedCount = null, term = TOPIC_TERM }) => {
  if (!nav) return null;
  const label = positionLabel(nav, term);
  return (
    <nav aria-label={`${term} navigation`} className="mt-20 pt-10 border-t border-gray-100 dark:border-white/10" data-testid="topic-navigation">
      {nav.isLast && (
        <div className={`mb-8 p-6 sm:p-8 rounded-3xl border text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-gradient-to-br from-purple-50 to-white border-purple-100'}`} data-testid="topic-end-state" role="status">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-[#5B21B6]/10 flex items-center justify-center mb-3">
            <Trophy size={22} className="text-[#5B21B6]" aria-hidden="true" />
          </div>
          <p className={`text-lg font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>You've reached the end of {nav.technology.title}</p>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {label} — this is the last {term.toLowerCase()} in this technology.
            {Number.isFinite(completedCount) ? ` You have completed ${completedCount} of ${nav.total} ${term.toLowerCase()}s.` : ''}
          </p>
          {onBackToTechnology && (
            <button type="button" onClick={onBackToTechnology} className={`mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm bg-[#5B21B6] text-white hover:bg-[#4C1D95] transition-colors shadow-lg shadow-purple-500/20 ${focusRing}`}>
              <BookOpen size={16} aria-hidden="true" /> Back to Technology
            </button>
          )}
        </div>
      )}

      <div className="flex items-stretch justify-between gap-3 sm:gap-4">
        {nav.previous ? (
          <NavCard direction="previous" entry={nav.previous} crossesSection={nav.previousCrossesSection} access={nav.previousAccess} isDark={isDark} onGo={onGo} term={term} />
        ) : <div className="flex-1 sm:max-w-[300px]" aria-hidden="true" />}

        <div className="hidden md:flex flex-col items-center justify-center px-2 text-center min-w-[120px]">
          <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
          <span className={`text-[10px] font-semibold mt-0.5 truncate max-w-[180px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`} title={nav.section.title}>
            {nav.section.title} · {nav.section.position} of {nav.section.total}
          </span>
        </div>

        {nav.next ? (
          <NavCard direction="next" entry={nav.next} crossesSection={nav.nextCrossesSection} access={nav.nextAccess} isDark={isDark} onGo={onGo} term={term} />
        ) : (
          <div className={`flex-1 sm:max-w-[300px] flex items-center justify-end gap-2 p-3 sm:p-4 rounded-2xl border border-dashed text-right ${isDark ? 'border-white/10 text-gray-400' : 'border-gray-200 text-gray-500'}`} data-testid="topic-nav-end" aria-live="polite">
            <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" aria-hidden="true" />
            <span className="text-xs font-bold">End of technology</span>
          </div>
        )}
      </div>
      <p className={`md:hidden mt-3 text-center text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
    </nav>
  );
};

/** The right-hand outline as a collapsible control for narrow screens (the xl sidebar stays as is). */
export const OnThisPageDropdown = ({ headings = [], activeHeading = '', onSelect, isDark = false, className = '' }) => {
  const [open, setOpen] = useState(false);
  if (!headings.length) return null;
  return (
    <div className={`xl:hidden ${className}`} data-testid="on-this-page-dropdown">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="elite-on-this-page"
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-[11px] font-black uppercase tracking-widest ${focusRing} ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-purple-100 text-[#5B21B6] hover:bg-purple-50'}`}
      >
        <List size={14} aria-hidden="true" /> On this page ({headings.length})
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <nav id="elite-on-this-page" aria-label="On this page" className={`mt-2 p-2 rounded-2xl border max-h-[50vh] overflow-y-auto ${isDark ? 'bg-[#0B0F1A] border-white/10' : 'bg-white border-gray-100 shadow-lg'}`}>
          {headings.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => { onSelect(h.id); setOpen(false); }}
              className={`block w-full text-left py-2 px-3 rounded-xl text-xs transition-colors ${focusRing} ${h.level === 3 ? 'pl-8' : 'font-bold'} ${
                activeHeading === h.id ? 'text-[#5B21B6] bg-[#5B21B6]/5' : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-[#5B21B6] hover:bg-purple-50'
              }`}
            >
              {h.text}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
};

export default TopicNavigationBar;
